import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { useSettings } from "../context/SettingsContext";
import { Plus, Minus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const { format } = useCurrency();
  const { t } = useSettings();

  React.useEffect(() => { document.title = "Cart – Cheap4Teach"; }, []);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-20 h-20 mx-auto text-[#FFC500]" strokeWidth={2.5} />
        <h1 className="text-4xl font-['Fredoka'] font-bold mt-6">{t("cart.empty_title", "Your cart is empty")}</h1>
        <p className="c4t-desc text-base sm:text-lg mt-2">{t("cart.empty_subtitle", "Let's fill it with classroom goodies!")}</p>
        <Link to="/shop" className="mt-6 inline-flex c4t-btn c4t-btn-pink" data-testid="empty-cart-shop-btn">{t("cart.empty_cta", "Browse the Shop")}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="cart-page">
      <h1 className="text-4xl sm:text-5xl font-['Fredoka'] font-bold mb-1">{t("cart.title", "Your Cart")}</h1>
      <p className="c4t-desc text-sm mb-5">All prices are in the currency you selected. Shipping calculated at checkout.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Cart items */}
        <div className="lg:col-span-2 space-y-3">
          {items.map(it => {
            const max = typeof it.stock === "number" ? it.stock : null;
            const atMax = max != null && it.quantity >= max;
            return (
              <div key={it.product_id} className="c4t-card !p-3" data-testid={`cart-item-${it.product_id}`}>
                {/* Top row: image + name/price */}
                <div className="flex gap-3 items-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 border-black/20 bg-white flex-shrink-0">
                    {it.image
                      ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-[#073B4C]/30 text-xs">No img</div>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Fredoka'] font-bold text-base sm:text-lg leading-tight">{it.name}</h3>
                    <p className="text-[#FF1F87] font-bold text-sm sm:text-base">{format(it.price)}</p>
                    {it.price_changed && (
                      <p className="text-xs text-[#FF6B1A] font-semibold mt-0.5">Price updated since you added this</p>
                    )}
                    {atMax && (
                      <p className="text-xs text-[#FF6B1A] font-semibold mt-0.5" data-testid={`cart-stock-cap-${it.product_id}`}>
                        Max in stock: {max}
                      </p>
                    )}
                  </div>
                </div>
                {/* Bottom row: qty + remove */}
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-black/10">
                  <div className="flex items-center border-2 border-black rounded-full">
                    <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-2" data-testid={`cart-dec-${it.product_id}`}>
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-8 text-center font-bold text-sm" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.quantity + 1)} disabled={atMax} className="p-2 disabled:opacity-40" data-testid={`cart-inc-${it.product_id}`}>
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <button onClick={() => removeItem(it.product_id)} className="p-2 text-gray-400 hover:text-[#FF1F87]" data-testid={`cart-remove-${it.product_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="c4t-card h-fit lg:sticky lg:top-24" data-testid="cart-summary">
          <h2 className="text-2xl font-['Fredoka'] font-bold">Summary</h2>
          <div className="mt-4 space-y-2 c4t-desc">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span className="font-bold text-black" data-testid="cart-subtotal">{format(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>Shipping</span>
              <span className="text-sm italic">Selected at checkout</span>
            </div>
          </div>
          <Link to="/checkout" className="mt-5 c4t-btn c4t-btn-pink w-full" data-testid="cart-checkout-btn">
            Checkout <ArrowRight className="w-5 h-5" />
          </Link>
          <Link to="/shop" className="mt-3 text-center block text-[#00A6E0] font-semibold hover:text-[#FF1F87] text-sm">
            Continue shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
